import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { closeAllTooltips, createMarkerElement } from '../ui/marker';
import { DEFINITIONS } from '../utils/constants';
import { getDistance, isValidCoordinates, setsAreEqual } from '../utils/stores';

/**
 * @description Creates markers only for stores within map bounds
 * @param {Array} stores - All stores
 * @param {Object} bounds - Google Maps LatLngBounds
 * @param {Object} LatLng - Google Maps LatLng constructor
 * @returns {Array} Filtered stores within bounds
 */
const getStoresInBounds = (stores, bounds, LatLng) => {
  if (!bounds || !bounds.contains) return stores;

  return stores.filter((store) => {
    const lat = Number(store.latitude);
    const lng = Number(store.longitude);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;

    const position = new LatLng(lat, lng);
    return bounds.contains(position);
  });
};

/**
 * @description Initializes store data and creates markers only for visible stores
 * @param {HTMLElement} context - The section container element
 * @param {Object} options - Configuration options
 * @param {Object} options.state - The application state object
 * @param {Object} options.loader - The Google Maps API loader
 * @param {Object} options.map - The Google Map instance
 * @param {Object} options.userPosition - The user's current position (latitude, longitude)
 * @returns {Promise<{stores: Array, tags: Array}>} - The stores and tags data
 */
export const initStores = async (context, { state, loader, map, userPosition }) => {
  const storesJsonElement = context.querySelector('[data-stores-json]');
  if (!storesJsonElement) return console.error('Store locator JSON data not found');

  const storesJson = storesJsonElement.textContent;
  if (!storesJson) {
    console.error('Store locator JSON is empty');
    return { stores: [], tags: [] };
  }

  try {
    const storesData = JSON.parse(storesJson);
    if (!Array.isArray(storesData) || storesData.length === 0) {
      console.error('Store locator JSON is invalid or empty');
      return { stores: [], tags: [] };
    }

    // Global click handler to close tooltips
    document.addEventListener('click', (e) => {
      const isMarkerClick = e.target.closest(`.${DEFINITIONS.slug}__marker`);
      if (!isMarkerClick) {
        closeAllTooltips();

        // Reset z-index of all markers
        context.querySelectorAll(`.${DEFINITIONS.slug}__marker`).forEach((marker) => {
          if (marker.parentNode) marker.parentNode.style.zIndex = '';
        });

        // Remove active class from all list items
        context.querySelectorAll(`.${DEFINITIONS.slug}__list-item.${DEFINITIONS.activeClass}`).forEach((item) => {
          item.classList.remove(DEFINITIONS.activeClass);
        });
      }
    });

    const { LatLng } = await loader.importLibrary('core');
    if (!LatLng) return console.error('Google Maps core library is not loaded');

    if (userPosition.coords) await loader.importLibrary('geometry');

    let stores = [...storesData];
    let tags = [];

    const { AdvancedMarkerElement } = await loader.importLibrary('marker');

    // Process stores and extract tags
    stores = stores
      .map((store) => {
        if (!isValidCoordinates({ lat: store.latitude, lng: store.longitude })) {
          return null;
        }
        const lat = parseFloat(store.latitude);
        const lng = parseFloat(store.longitude);

        if (userPosition.coords) {
          getDistance({ lat: userPosition.coords.lat, lng: userPosition.coords.lng }, { lat, lng }, loader).then(
            (d) => {
              store.distance = d;
            },
          );
        } else {
          store.distance = null;
        }

        store.latitude = lat;
        store.longitude = lng;

        if (store.tags && Array.isArray(store.tags)) {
          store.tags.forEach(({ label }) => {
            if (label && !tags.includes(label)) tags.push(label);
          });
        }

        return store;
      })
      .filter((store) => store !== null);

    // Get initial bounds - use user position or calculate from stores
    let initialBounds;
    if (userPosition.coords) {
      // Create bounds around user position with reasonable radius
      const userLatLng = new LatLng(userPosition.coords.lat, userPosition.coords.lng);
      initialBounds = new window.google.maps.LatLngBounds(userLatLng, userLatLng);

      // Extend bounds by ~50km in each direction
      const offsetLat = 0.45; // Approximately 50km
      const offsetLng = 0.45;
      initialBounds.extend(new LatLng(userPosition.coords.lat + offsetLat, userPosition.coords.lng + offsetLng));
      initialBounds.extend(new LatLng(userPosition.coords.lat - offsetLat, userPosition.coords.lng - offsetLng));
    } else {
      // Calculate bounds from all stores
      initialBounds = new window.google.maps.LatLngBounds();
      stores.forEach((store) => {
        initialBounds.extend(new LatLng(store.latitude, store.longitude));
      });
    }

    // Function to create markers for stores in bounds
    const createMarkersInBounds = (bounds) => {
      const visibleStores = getStoresInBounds(stores, bounds, LatLng);
      const markers = [];

      visibleStores.forEach((store) => {
        const position = new LatLng(store.latitude, store.longitude);

        if (!AdvancedMarkerElement) return console.error('Google Maps Marker library is not loaded');

        // Check if marker already exists
        if (state.markers[store.uid]) {
          markers.push(state.markers[store.uid]);
          return;
        }

        const marker = new AdvancedMarkerElement({
          content: createMarkerElement(context, { store }),
          map: null, // Don't add to map yet
          position,
          title: store.name,
        });

        marker.addListener('click', () => {
          map.setCenter(position);
          map.setZoom(15);

          const listItem = context.querySelector(`.${DEFINITIONS.slug}__list-item[data-id="${store.uid}"]`);
          if (listItem) {
            const activeItem = context.querySelector(`.${DEFINITIONS.slug}__list-item.${DEFINITIONS.activeClass}`);
            if (activeItem) activeItem.classList.remove(DEFINITIONS.activeClass);
            listItem.classList.add(DEFINITIONS.activeClass);
          }
        });

        // Store marker by ID
        state.markers[store.uid] = marker;
        markers.push(marker);
      });

      return markers;
    };

    // Create initial markers
    const initialMarkers = createMarkersInBounds(initialBounds);

    // Initialize MarkerClusterer with visible markers only
    state.clusterer = new MarkerClusterer({
      map,
      markers: initialMarkers,
      renderer: {
        render: ({ count, position }) => {
          // Wrapper element
          const wrapper = document.createElement('div');
          wrapper.className = `${DEFINITIONS.slug}__cluster-wrapper`;

          wrapper.innerHTML = `<svg alt="${count} stores" class="${DEFINITIONS.slug}__cluster-icon"><use href="#icon-cluster"></use></svg>`;

          // Label overlay
          const label = document.createElement('div');
          label.className = `${DEFINITIONS.slug}__cluster-label`;
          label.textContent = count > 999 ? '999+' : String(count);
          wrapper.appendChild(label);

          // Return AdvancedMarkerElement
          return new AdvancedMarkerElement({
            position,
            content: wrapper,
            zIndex: 1000 + count, // Ensure clusters are above markers
          });
        },
      },
    });

    // Listen for bounds changes and load markers dynamically
    map.addListener('idle', () => {
      const currentBounds = map.getBounds();
      if (!currentBounds) return;

      const newMarkers = createMarkersInBounds(currentBounds);

      if (
        setsAreEqual(
          new Set(newMarkers.map((m) => m.position.toString())),
          new Set(state.clusterer.markers.map((m) => m.position.toString())),
        )
      ) {
        // No changes in markers, skip update
        return;
      }

      // Update clusterer with all created markers
      state.clusterer.clearMarkers();
      state.clusterer.addMarkers(newMarkers);
    });

    // Set initial map view with padding to avoid grey areas
    if (userPosition.coords) {
      map.setCenter(new LatLng(userPosition.coords.lat, userPosition.coords.lng));
      map.setZoom(12); // Reasonable zoom level
    } else if (initialBounds && !initialBounds.isEmpty()) {
      // Fit bounds with padding to prevent grey areas
      map.fitBounds(initialBounds, {
        padding: { top: 50, right: 50, bottom: 50, left: 50 },
      });

      // Ensure zoom is not too close
      setTimeout(() => {
        const currentZoom = map.getZoom();
        if (currentZoom > 18) {
          map.setZoom(18);
        }
      }, 100);
    }

    return { stores, tags };
  } catch (error) {
    console.error('Error parsing store locator JSON data:', error);
    return { stores: [], tags: [] };
  }
};
